// Event time helpers — mirror of healthy-pet-react-native `src/shared/utils/date.ts` + `remindDateTime.ts`.
// Dates in forms are DD.MM.YYYY strings; time is a separate HH:mm string ('' = not set / as event).
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

/** remindType «за 1 час»: в форме хранится день события, момент = время события − 1 час. */
export const REMIND_HOUR_BEFORE = "1 hour before";

const HOUR_MS = 60 * 60 * 1000;

export const parseTimeHHmm = (value) => {
  if (typeof value !== "string") return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  if (hours > 23 || minutes > 59) return null;
  return { hours, minutes };
};

export const formatTimeHHmm = (d) =>
  `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

/**
 * Текущее время, округлённое вниз до 5 минут (как в RN).
 * Вниз: событие на сегодня со временем по умолчанию должно остаться «уже было».
 */
export const getDefaultEventTime = (now = new Date()) => {
  const d = new Date(now.getTime());
  d.setMinutes(Math.floor(d.getMinutes() / 5) * 5, 0, 0);
  return formatTimeHHmm(d);
};

/** Копия даты с временем HH:mm; без валидного времени — начало дня. */
export const applyTimeToDate = (date, time) => {
  const d = new Date(date.getTime());
  const parsed = parseTimeHHmm(time);
  d.setHours(parsed ? parsed.hours : 0, parsed ? parsed.minutes : 0, 0, 0);
  return d;
};

export const parseFormDay = (str) => {
  if (!str) return null;
  const d = dayjs(str, "DD.MM.YYYY", true);
  return d.isValid() ? d.toDate() : null;
};

/**
 * Событие в будущем: день после сегодня или сегодня, но время (HH:mm) ещё не наступило.
 * Без времени (старые события) — только по дню.
 */
export const isFutureEventMoment = (dateStr, time, now = new Date()) => {
  const day = parseFormDay(dateStr);
  if (!day) return false;
  const today = new Date(now.getTime());
  today.setHours(0, 0, 0, 0);
  if (day.getTime() > today.getTime()) return true;
  if (day.getTime() < today.getTime() || !parseTimeHHmm(time)) return false;
  return applyTimeToDate(day, time).getTime() > now.getTime();
};

export const getDeviceTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

/** Можно ли выбрать своё время напоминания: у всех типов, кроме «за 1 час» и «никогда». */
export const remindSupportsOwnTime = (type) =>
  !!type && type !== "Never" && type !== REMIND_HOUR_BEFORE;

/**
 * Момент напоминания по дню из формы (Date или null).
 * Своё время — если выбрано и тип его допускает; иначе время события;
 * «за 1 час» — на час раньше времени события. Без времени — начало дня (старые события).
 */
export const resolveRemindDateTime = (day, type, eventTime, remindTime) => {
  if (!day) return null;
  if (remindSupportsOwnTime(type) && parseTimeHHmm(remindTime)) {
    return applyTimeToDate(day, remindTime);
  }
  const at = applyTimeToDate(day, eventTime);
  if (type === REMIND_HOUR_BEFORE && parseTimeHHmm(eventTime)) {
    return new Date(at.getTime() - HOUR_MS);
  }
  return at;
};

/** Обратно к resolveRemindDateTime: сохранённый момент → день, который хранится в форме. */
export const remindFormDayFromStored = (stored, type) =>
  type === REMIND_HOUR_BEFORE ? new Date(stored.getTime() + HOUR_MS) : stored;

/** Время следующего события (повтора): своё `repeatTime`, иначе время текущего события. */
export const resolveRepeatTime = (eventTime, repeatTime) =>
  parseTimeHHmm(repeatTime) ? repeatTime : eventTime || "";

/** Своё время, только если оно отличается от базового; иначе '' («как у события»). */
export const ownTime = (time, baseTime) => (time && time !== baseTime ? time : "");

/** Момент напоминания для подписи: { date: 'DD.MM.YYYY', time: 'HH:mm' | null } или null. */
export const resolveRemindDisplay = (dayStr, type, eventTime, remindTime) => {
  const resolved = resolveRemindDateTime(parseFormDay(dayStr), type, eventTime, remindTime);
  if (!resolved) return null;
  const hasTime =
    parseTimeHHmm(eventTime) != null ||
    (remindSupportsOwnTime(type) && parseTimeHHmm(remindTime) != null);
  return {
    date: dayjs(resolved).format("DD.MM.YYYY"),
    time: hasTime ? formatTimeHHmm(resolved) : null,
  };
};

/** 12-часовой формат по локали браузера. */
export const uses12HourClock = () => {
  try {
    const cycle = new Intl.DateTimeFormat(undefined, { hour: "numeric" }).resolvedOptions().hourCycle;
    return cycle === "h12" || cycle === "h11";
  } catch {
    return false;
  }
};

/** HH:mm → строка для показа (24h или 12h по локали). */
export const formatDisplayTime = (time) => {
  const parsed = parseTimeHHmm(time);
  if (!parsed) return "";
  if (!uses12HourClock()) return time;
  const h = parsed.hours % 12 || 12;
  const suffix = parsed.hours < 12 ? "AM" : "PM";
  return `${h}:${String(parsed.minutes).padStart(2, "0")} ${suffix}`;
};

/** Ключ сортировки события: день + время (без времени — начало дня). */
export const eventSortKey = (event) => {
  const day = parseFormDay(event?.date);
  return day ? applyTimeToDate(day, event?.time).getTime() : 0;
};

/** «DD.MM.YYYY, HH:mm» или только дата, если времени нет. */
export const formatDateWithTime = (dateStr, time) => {
  if (!dateStr) return "";
  const t = formatDisplayTime(time);
  return t ? `${dateStr}, ${t}` : dateStr;
};
